import type { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

// import { TreeSearchAction } from "./reducer";
import * as treeSearchApi from "@/shop/api/treeSearch";
import { parseSortString } from "@/shop/components/common/SortIndicator/parseSortString";
import type { SortDirection } from "@/shop/types/catalog";

import * as TreeSearchActions from "./actions";
import { getAdvancedPOSTLoading, getSearchResultsUrl, getSearchTableQuery } from "./selectors";
import type { ITreeMatsQuery, ITreeMatsSearchResult } from "./types";

function* fetchTreeMats(action: ActionType<typeof TreeSearchActions.fetchResults>): SagaIterator {
  const { url, query, advancedPOSTLoading } = action.payload;
  try {
    // flag the materials list as fetching
    yield put(TreeSearchActions.fetchResultsStart({ url, query }));
    // call the api to retrieve the materials list
    const materialsResult = (yield call(treeSearchApi.fetchTreeMaterials, url, query, advancedPOSTLoading)) as ITreeMatsSearchResult;

    if (query.sort) {
      const sortObj = parseSortString(query.sort);
      const header = materialsResult.header.find(({ id }) => id === sortObj?.code);
      if (header) {
        header.sort = sortObj!.direction as SortDirection;
      }
    }

    // put action to save data in state
    yield put(TreeSearchActions.fetchResultsSuccess(materialsResult));
  } catch (err) {
    console.error(err);
    // fetch failed, handle the error
    yield put(TreeSearchActions.fetchResultsFail());
  }
}

function* submitTreeSearch(action: ActionType<typeof TreeSearchActions.submitSearch>): SagaIterator {
  const nextQueryWhere = action.payload;
  // retrieve current state
  const currentQuery = (yield select(getSearchTableQuery)) as ITreeMatsQuery;
  const materialsUrl = (yield select(getSearchResultsUrl)) as string;
  const advancePOSTLoadingQuery = (yield select(getAdvancedPOSTLoading)) as boolean;

  // set filter and offset
  const nextQuery: ITreeMatsQuery = { ...currentQuery, offset: 0, where: nextQueryWhere };
  yield put(TreeSearchActions.fetchResults({ url: materialsUrl, query: nextQuery, advancedPOSTLoading: advancePOSTLoadingQuery }));
}

function* initTreeSearch(action: ActionType<typeof TreeSearchActions.initSearch>): SagaIterator {
  const { url, id, filter, pager, advancedPOSTLoading } = action.payload;
  const initialPager = isNaN(pager!.offset) ? {} : pager;
  const currentQuery = (yield select(getSearchTableQuery)) as ITreeMatsQuery;
  const nextQuery: ITreeMatsQuery = { ...currentQuery, id, offset: 0, where: filter!, ...initialPager };
  yield put(TreeSearchActions.fetchResults({ url, query: nextQuery, advancedPOSTLoading: advancedPOSTLoading! }));
}

function* changePage(action: ActionType<typeof TreeSearchActions.changePage>): SagaIterator {
  const targetPage: number = action.payload;
  const materialsUrl = (yield select(getSearchResultsUrl)) as string;
  const currentQuery = (yield select(getSearchTableQuery)) as ITreeMatsQuery;
  const offset = (targetPage - 1) * currentQuery.limit;
  const nextQuery: ITreeMatsQuery = { ...currentQuery, offset };
  const advancePOSTLoadingQuery = (yield select(getAdvancedPOSTLoading)) as boolean;
  yield put(TreeSearchActions.fetchResults({ url: materialsUrl, query: nextQuery, advancedPOSTLoading: advancePOSTLoadingQuery }));
}

function* changePageSize(action: ActionType<typeof TreeSearchActions.changePageSize>): SagaIterator {
  const limit: number = action.payload;
  const offset = 0;
  const materialsUrl = (yield select(getSearchResultsUrl)) as string;
  const currentQuery = (yield select(getSearchTableQuery)) as ITreeMatsQuery;
  const nextQuery: ITreeMatsQuery = { ...currentQuery, offset, limit };
  const advancePOSTLoadingQuery = (yield select(getAdvancedPOSTLoading)) as boolean;
  yield put(TreeSearchActions.fetchResults({ url: materialsUrl, query: nextQuery, advancedPOSTLoading: advancePOSTLoadingQuery }));
}

function* changeSort(action: ActionType<typeof TreeSearchActions.changeSort>): SagaIterator {
  const nextSort: string = action.payload;
  const materialsUrl = (yield select(getSearchResultsUrl)) as string;
  const currentQuery = (yield select(getSearchTableQuery)) as ITreeMatsQuery;
  const offset = 0;
  const nextQuery: ITreeMatsQuery = { ...currentQuery, offset, sort: nextSort };
  const advancePOSTLoadingQuery = (yield select(getAdvancedPOSTLoading)) as boolean;
  yield put(TreeSearchActions.fetchResults({ url: materialsUrl, query: nextQuery, advancedPOSTLoading: advancePOSTLoadingQuery }));
}

function* changeSortCardsView(action: ActionType<typeof TreeSearchActions.changeSortCardsView>): SagaIterator {
  const nextSort: { id: string; sortDirection: string } = action.payload;
  const materialsUrl = (yield select(getSearchResultsUrl)) as string;
  const currentQuery = (yield select(getSearchTableQuery)) as ITreeMatsQuery;
  const offset = 0;
  const sort = nextSort?.id + " " + nextSort?.sortDirection;
  const nextQuery: ITreeMatsQuery = { ...currentQuery, offset, sort };
  const advancePOSTLoadingQuery = (yield select(getAdvancedPOSTLoading)) as boolean;
  yield put(TreeSearchActions.fetchResults({ url: materialsUrl, query: nextQuery, advancedPOSTLoading: advancePOSTLoadingQuery }));
}

export default function* partTreeSearchSaga(): SagaIterator {
  yield takeLatest(getType(TreeSearchActions.fetchResults), fetchTreeMats);
  yield takeLatest(getType(TreeSearchActions.submitSearch), submitTreeSearch);
  yield takeLatest(getType(TreeSearchActions.initSearch), initTreeSearch);
  yield takeLatest(getType(TreeSearchActions.changePage), changePage);
  yield takeLatest(getType(TreeSearchActions.changePageSize), changePageSize);
  yield takeLatest(getType(TreeSearchActions.changeSort), changeSort);
  yield takeLatest(getType(TreeSearchActions.changeSortCardsView), changeSortCardsView);
}
